<script setup>
import Table from '@/@core/components/Table.vue'
import { AuditStatus, auditStatusColor, auditStatusTitle } from '@/enums/incident/audit.status'
import { useRouter } from 'vue-router'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    name: 'incident-audit',
  },  
})

const router = useRouter()

// Data table options
const searchQuery = ref()
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  // { title: '', key: 'data-table-expand' },
  {
    title: 'Title',
    key: 'title',
  },
  {
    title: 'department',
    key: 'department',
  },

  // {
  //   title: 'Repeatable Duration',
  //   key: 'repeat_duration',
  // },
  {
    title: 'Auditors',
    key: 'auditors',
  },
  {
    title: 'Verifiers',
    key: 'verifiers',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: auditListing,
  execute: fetchAudits,
} = await useApi(createUrl('/masters/incident-audit-schedules', {
  query: {
    paged: 1,
    q: searchQuery,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const audits = computed(() => auditListing.value?.data)
const totalAudits = computed(() => audits.value?.meta?.total)
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="audits"
        :items-length="totalAudits"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.title="{ item }">
          {{ item?.audit?.title }}
        </template>

        <template #item.department="{ item }">
          {{ item?.department?.name }}
        </template>

        <template #item.status="{ item }">
          <VChip
            variant="tonal"
            class="text-capitalize"
            :color="auditStatusColor(item.status)"
          >
            {{ auditStatusTitle(item.status) }}
          </VChip>
        </template>

        <template #item.auditors="{ item }">
          <div class="v-avatar-group">
            <template
              v-for="(user, index) in item.auditors"
              :key="index"
            >
              <VAvatar
                size="36"
                :color="$vuetify.theme.current.dark ? '#f6f6f7' : '#4A5072'"
              >
                <VTooltip
                  activator="parent"
                  location="end"
                >
                  {{ user.name }}
                </VTooltip>
                <VImg 
                  v-if="user.avatar"
                  :src="user.avatar"
                />
                <span v-else>
                  {{ avatarText(user.name) }}
                </span>
              </VAvatar>
            </template>
          </div>
        </template>

        <template #item.verifiers="{ item }">
          <div class="v-avatar-group">
            <template
              v-for="(user, index) in item.verifiers"
              :key="index"
            >
              <VAvatar
                size="36"
                :color="$vuetify.theme.current.dark ? '#f6f6f7' : '#4A5072'"
              >
                <VTooltip
                  activator="parent"
                  location="end"
                >
                  {{ user.name }}
                </VTooltip>
                <VImg 
                  v-if="user.avatar"
                  :src="user.avatar"
                />
                <span v-else>
                  {{ avatarText(user.name) }}
                </span>
              </VAvatar>
            </template>
          </div>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <!--
            <RouterLink :to="{ name: 'app-incident-master-audit-questionnaire', params: { id: item.id } }">
            <VBtn
            variant="tonal"
            density="compact"
            >
            Question Set
            </VBtn>
            </RouterLink> 
          -->

          <IconBtn
            :disabled="AuditStatus.DRAFT !== item.status"
            @click="router.push({ name: 'app-incident-audit-entry', params: { id: item.id } })"
          >
            <VIcon icon="tabler-notes" />
          </IconBtn>

          <IconBtn @click="router.push({ name: 'app-incident-audit-view', params: { id: item.id } })">
            <VIcon icon="tabler-eye" />
          </IconBtn>

          <!--
            <IconBtn 
            v-if="$can('update', 'master.audit')"
            @click="showAudit(item)"
            >
            <VIcon icon="tabler-edit" />
            </IconBtn>

            <IconBtn 
            v-if="$can('delete', 'master.audit')"
            @click="showDeleteDialog(item)"
            >
            <VIcon icon="tabler-trash" />
            </IconBtn> 
          -->
        </template>

        <!-- Audit Schedules -->
        <template #expanded-row="{ item }">
          <tr class="v-data-table__tr evel-table-custom-bg-color">
            <td
              colspan="12"
              class="pb-4"
              style="background-color: #f7f7f7;"
            >
              <VCard variant="text">
                <VCardText>
                  <p class="text-h6">
                    Schedule Audits
                  </p>
                  <Table
                    :columns="[
                      { key: 'start_date', title: 'Start Date' },
                      { key: 'end_date', title: 'End Date' },
                      { key: 'Audities', title: 'Audities' },
                    ]"
                    :items="[]"
                  />
                </VCardText>
              </VCard>
            </td>
          </tr>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalAudits) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalAudits / itemsPerPage)"
          :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalAudits / itemsPerPage)"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>

          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>
  </section>
</template>
